import {
  useState,
  useCallback,
  createContext,
  useContext,
  ReactNode,
} from "react";
import { colors, spacing, radius } from "./tokens";

type ToastKind = "success" | "error" | "info" | "warning";
type Toast = { id: string; kind: ToastKind; message: string };
type Ctx = { show: (msg: string, kind?: ToastKind) => void };

const ToastContext = createContext<Ctx | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const show = useCallback((message: string, kind: ToastKind = "info") => {
    const id = Math.random().toString(36).slice(2);
    setToasts((prev) => [...prev, { id, kind, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      <div
        aria-live="polite"
        style={{
          position: "fixed",
          bottom: spacing.xl,
          left: 0,
          right: 0,
          display: "flex",
          flexDirection: "column",
          gap: spacing.sm,
          alignItems: "center",
          zIndex: 1000,
          pointerEvents: "none",
        }}
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            role="status"
            style={{
              background:
                t.kind === "error"
                  ? colors.danger
                  : t.kind === "success"
                    ? colors.success
                    : colors.surfaceElevated,
              color: colors.text,
              padding: `${spacing.sm}px ${spacing.lg}px`,
              borderRadius: radius.md,
              maxWidth: 400,
              pointerEvents: "auto",
            }}
          >
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}
